/**
 * PATH: packages/email-hub/src/feature/components/charts/ComposeChartModal.tsx
 * FILE_ID: EMAILHUB-CHARTS-MODAL-001
 * PURPOSE: The composer's chart & flowchart builder — draft editors on the
 *          left, live library preview (recharts / React Flow) on the right,
 *          output as a PNG attachment or an inline image in the email body.
 * WHY: FEAT-2026-07-03-COMPOSE-CHARTS — quick graph creation in compose,
 *      reusing the Jobs BI chart library (recharts) and the pre-existing
 *      flow libraries (@xyflow/react + dagre) instead of rebuilding.
 * FLOW:
 *   draft (editor) ─▶ parseChartSpec/parseFlowSpec ─▶ spec ─▶ <preview/>
 *   Attach → preview DOM → PNG File → onAttach (composer addFiles)
 *   Insert → PNG File → data-URL <img> → onInsertHtml (TipTap)
 * RELATED:
 *   - chartSpec.ts: the validated spec contract (shared with AI generation)
 *   - chartRender.tsx: renderers + PNG export
 *   - ComposeWorkbookModal.tsx: opens this pre-seeded from sheet data
 */
import { useMemo, useRef, useState } from 'react'
import { BarChart3, Loader2, Paperclip, Sparkles, Workflow, X } from 'lucide-react'

import {
  CHART_KINDS,
  parseChartSpec,
  parseFlowSpec,
  type ChartKind,
  type ChartSeries,
  type ChartSpec,
} from './chartSpec'
import {
  ChartSpecView,
  FlowSpecView,
  chartContainerToPngFile,
  elementToPngFile,
  pngFileToEmailInsert,
} from './chartRender'
import { ChartDataEditor, emptyChartDraft, type ChartDraft } from './ChartDataEditor'
import { FlowEditor, emptyFlowDraft, type FlowDraft } from './FlowEditor'
import { generateSpecFromPrompt } from './aiChartSpec'

export interface ComposeChartModalProps {
  /** Seed for the attachment filename + chart title. */
  defaultName?: string
  /** Pre-seeded data (e.g. from the spreadsheet builder's active sheet). */
  initialChart?: { series: ChartSeries[]; data: ChartSpec['data'] } | null
  onAttach: (file: File) => void
  onInsertHtml: (html: string) => void
  onClose: () => void
  notify?: (message: string, kind: 'success' | 'error') => void
}

function draftFromInitial(
  initial: NonNullable<ComposeChartModalProps['initialChart']>,
): ChartDraft {
  return {
    seriesNames: initial.series.map((s) => s.name),
    rows: initial.data.map((row) => ({
      label: String(row.label ?? ''),
      values: Object.fromEntries(
        initial.series
          .filter((s) => row[s.name] !== undefined)
          .map((s) => [s.name, String(row[s.name])]),
      ),
    })),
  }
}

export function ComposeChartModal({
  defaultName,
  initialChart,
  onAttach,
  onInsertHtml,
  onClose,
  notify,
}: ComposeChartModalProps) {
  const [mode, setMode] = useState<'chart' | 'flow'>('chart')
  const [kind, setKind] = useState<ChartKind>('bar')
  const [title, setTitle] = useState(defaultName?.trim() || '')
  const [chartDraft, setChartDraft] = useState<ChartDraft>(() =>
    initialChart ? draftFromInitial(initialChart) : emptyChartDraft(),
  )
  const [flowDraft, setFlowDraft] = useState<FlowDraft>(() => emptyFlowDraft())
  const [busy, setBusy] = useState(false)
  // AI-prompted generation (FEAT-2026-07-03-COMPOSE-AI-CHARTS): the model
  // fills the SAME validated spec the manual editors produce — never markup.
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const flowRef = useRef<HTMLDivElement>(null)

  const chartParse = useMemo(
    () =>
      parseChartSpec({
        kind,
        title,
        series: chartDraft.seriesNames.map((name) => ({ name })),
        data: chartDraft.rows.map((r) => ({ label: r.label, ...r.values })),
      }),
    [kind, title, chartDraft],
  )
  const flowParse = useMemo(
    () =>
      parseFlowSpec({
        title,
        nodes: flowDraft.nodes.map((n) => ({ id: n, label: n })),
        edges: flowDraft.edges.map((e) => ({ from: e.from, to: e.to, label: e.label || undefined })),
      }),
    [title, flowDraft],
  )
  const activeOk = mode === 'chart' ? chartParse.ok : flowParse.ok
  const activeError =
    mode === 'chart'
      ? (chartParse.ok ? null : chartParse.error)
      : (flowParse.ok ? null : flowParse.error)

  const handleAiGenerate = async () => {
    setAiBusy(true)
    setAiError(null)
    // Pass the current draft as revision context so "make it a pie" or
    // "add a Q4 column" edits the existing data instead of inventing new.
    const contextJson =
      mode === 'chart' && chartParse.ok
        ? JSON.stringify({ kind, title, series: chartParse.spec.series, data: chartParse.spec.data })
        : mode === 'flow' && flowParse.ok
          ? JSON.stringify({ title, nodes: flowParse.spec.nodes, edges: flowParse.spec.edges })
          : undefined
    const res = await generateSpecFromPrompt({ mode, prompt: aiPrompt, contextJson })
    setAiBusy(false)
    if (!res.ok) { setAiError(res.error); return }
    if (res.mode === 'chart') {
      setKind(res.spec.kind)
      if (res.spec.title) setTitle(res.spec.title)
      setChartDraft(draftFromInitial({ series: res.spec.series, data: res.spec.data }))
    } else {
      if (res.spec.title) setTitle(res.spec.title)
      setFlowDraft({
        nodes: res.spec.nodes.map((n) => n.label),
        edges: res.spec.edges.map((e) => {
          const from = res.spec.nodes.find((n) => n.id === e.from)?.label ?? e.from
          const to = res.spec.nodes.find((n) => n.id === e.to)?.label ?? e.to
          return { from, to, label: e.label ?? '' }
        }),
      })
    }
    setAiPrompt('')
  }

  const makePng = async (): Promise<File | null> => {
    const base = title || (mode === 'chart' ? 'Chart' : 'Flowchart')
    try {
      if (mode === 'chart') {
        if (!chartRef.current) return null
        return await chartContainerToPngFile(chartRef.current, base)
      }
      const canvas = flowRef.current?.querySelector('.react-flow') as HTMLElement | null
      if (!canvas) return null
      return await elementToPngFile(canvas, base)
    } catch {
      notify?.('Could not export the image — try again.', 'error')
      return null
    }
  }

  const handleAttach = async () => {
    setBusy(true)
    const file = await makePng()
    setBusy(false)
    if (!file) return
    onAttach(file)
    onClose()
  }

  const handleInsert = async () => {
    setBusy(true)
    const file = await makePng()
    if (!file) { setBusy(false); return }
    try {
      // Route through the inline-image guard: small → embedded <img>,
      // oversized → attachment + a toast saying why (never a doomed
      // raw data-URL the recipient's client would strip).
      const insert = await pngFileToEmailInsert(file)
      if (insert.kind === 'inline') {
        onInsertHtml(insert.html)
      } else {
        onAttach(insert.file)
        notify?.(insert.reason, 'success')
      }
      onClose()
    } catch {
      notify?.('Could not insert the image — try attaching instead.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const tabCls = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ${
      active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
    }`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Chart builder"
      data-testid="compose-chart-modal"
    >
      <div className="flex h-[85vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2.5">
          <button type="button" onClick={() => setMode('chart')} className={tabCls(mode === 'chart')}>
            <BarChart3 className="h-3.5 w-3.5" /> Chart
          </button>
          <button type="button" onClick={() => setMode('flow')} className={tabCls(mode === 'flow')}>
            <Workflow className="h-3.5 w-3.5" /> Flowchart
          </button>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={mode === 'chart' ? 'Chart title' : 'Flowchart title'}
            aria-label="Title"
            className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-2 py-1 text-sm font-medium text-foreground hover:border-border focus:border-primary focus:outline-none"
          />
          {mode === 'chart' && (
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ChartKind)}
              aria-label="Chart type"
              className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground focus:border-primary focus:outline-none"
            >
              {CHART_KINDS.map((k) => (
                <option key={k} value={k}>{k[0].toUpperCase() + k.slice(1)}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            aria-label="Close chart builder"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* AI generation bar — prompt → validated spec → same renderer */}
        <div className="shrink-0 border-b border-border px-4 py-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
            <input
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !aiBusy && aiPrompt.trim()) void handleAiGenerate() }}
              placeholder={
                mode === 'chart'
                  ? 'Describe the chart… e.g. "bar chart of budget vs actual for Jan-Mar: 100/120, 90/80, 110/95"'
                  : 'Describe the process… e.g. "quote approval: draft → review → client sign-off, rejected goes back to draft"'
              }
              aria-label="Generate with AI"
              className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none"
            />
            <button
              type="button"
              onClick={() => void handleAiGenerate()}
              disabled={aiBusy || aiPrompt.trim() === ''}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50"
            >
              {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Generate
            </button>
          </div>
          {aiError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{aiError}</p>}
        </div>

        {/* Body: editor | preview */}
        <div className="flex min-h-0 flex-1">
          <div className="w-[340px] shrink-0 overflow-y-auto border-r border-border p-3">
            {mode === 'chart' ? (
              <ChartDataEditor draft={chartDraft} onChange={setChartDraft} />
            ) : (
              <FlowEditor draft={flowDraft} onChange={setFlowDraft} />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-center justify-center overflow-auto bg-white p-4">
            {activeError ? (
              <p className="max-w-sm text-center text-sm text-muted-foreground">{activeError}</p>
            ) : mode === 'chart' && chartParse.ok ? (
              <div ref={chartRef} className="bg-white">
                {title && <div className="pb-1 text-center text-sm font-semibold text-neutral-800">{title}</div>}
                <ChartSpecView spec={chartParse.spec} />
              </div>
            ) : mode === 'flow' && flowParse.ok ? (
              <div ref={flowRef} className="h-full w-full bg-white">
                <FlowSpecView spec={flowParse.spec} />
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-4 py-2.5">
          <button
            type="button"
            onClick={handleInsert}
            disabled={!activeOk || busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart3 className="h-3.5 w-3.5" />}
            Insert into email
          </button>
          <button
            type="button"
            onClick={handleAttach}
            disabled={!activeOk || busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
            Attach .png
          </button>
        </div>
      </div>
    </div>
  )
}
